import { useMemo } from 'react';
import {
  useSharedValue,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  interpolate,
  Extrapolation,
} from 'react-native-reanimated';
import { Spacing } from '@/constants/spacing';

export type UseCollapsibleHeaderParams = {
  maxHeight: number;
  minHeight: number;
};

export type UseCollapsibleHeaderReturn = {
  scrollHandler: ReturnType<typeof useAnimatedScrollHandler>;
  headerTranslateStyle: ReturnType<typeof useAnimatedStyle>;
  dateAnimatedStyle: ReturnType<typeof useAnimatedStyle>;
  editButtonAnimatedStyle: ReturnType<typeof useAnimatedStyle>;
};

/**
 * Custom hook for sticky header scroll behavior
 *
 * Header scrolls up with content until only minHeight is visible, then stops.
 * Uses translateY for natural scroll feel.
 *
 * @param maxHeight - Full header height
 * @param minHeight - Visible header height when stuck
 */
export function useCollapsibleHeader({
  maxHeight,
  minHeight,
}: UseCollapsibleHeaderParams): UseCollapsibleHeaderReturn {
  const scrollY = useSharedValue(0);
  const maxTranslate = maxHeight - minHeight;

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
    },
  });

  // Header scrolls up with content, stops when only minHeight visible
  const headerTranslateStyle = useAnimatedStyle(() => {
    const translateY = interpolate(
      scrollY.value,
      [0, maxTranslate],
      [0, -maxTranslate],
      Extrapolation.CLAMP
    );

    return {
      transform: [{ translateY }],
    };
  });

  // Date stays at bottom, no fancy animations
  const dateAnimatedStyle = useAnimatedStyle(() => {
    return {
      bottom: Spacing.sm,
    };
  });

  // Edit button fades out as header collapses
  const editButtonAnimatedStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      scrollY.value,
      [0, maxTranslate * 0.5],
      [1, 0],
      Extrapolation.CLAMP
    );

    return { opacity };
  });

  return useMemo(
    () => ({
      scrollHandler,
      headerTranslateStyle,
      dateAnimatedStyle,
      editButtonAnimatedStyle,
    }),
    [scrollHandler, headerTranslateStyle, dateAnimatedStyle, editButtonAnimatedStyle]
  );
}
